import { useState, useEffect } from 'react'
import { PageHeader, StatusBadge, Modal, EmptyState } from '../components/UI'
import { get, post, put } from '../utils/api'
import { formatDate } from '../utils/helpers'
import { useAuth } from '../context/AuthContext'
import { IconPlus, IconUsersGroup } from '@tabler/icons-react'

export default function TeamPage() {
  const { user } = useAuth()
  const [team, setTeam] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [formData, setFormData] = useState({})
  const [saving, setSaving] = useState(false)

  const load = async () => { try { setTeam(await get('/team')) } catch {} finally { setLoading(false) } }
  useEffect(() => { load() }, [])

  const isAdmin = user?.role === 'admin'

  const handleCreate = async () => {
    setSaving(true)
    try { await post('/team', formData); setShowAdd(false); setFormData({}); load() }
    catch (err) { alert(err.message) } finally { setSaving(false) }
  }

  const toggleActive = async (member) => {
    try { await put(`/team/${member.id}`, { ...member, is_active: !member.is_active }); load() }
    catch (err) { alert(err.message) }
  }

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>

  return (
    <>
      <PageHeader title="Team" subtitle={`${team.length} team members`}
        actions={isAdmin && <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}><IconPlus size={16} className="me-1" />Add Member</button>} />

      {team.length === 0 ? <EmptyState icon={IconUsersGroup} title="No team members" /> : (
        <div className="row row-cards">
          {team.map(m => (
            <div key={m.id} className="col-md-6 col-xl-4">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-3">
                    <span className="avatar avatar-md rounded-circle bg-primary-lt me-3">
                      {m.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </span>
                    <div>
                      <h4 className="card-title mb-0">{m.name}</h4>
                      <div className="text-secondary" style={{ fontSize: '0.85rem' }}>{m.title}</div>
                    </div>
                    <div className="ms-auto">
                      <span className={`badge bg-${m.is_active ? 'green' : 'red'}-lt`}>{m.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                  </div>
                  <div className="datagrid">
                    <div className="datagrid-item"><div className="datagrid-title">Email</div><div className="datagrid-content">{m.email}</div></div>
                    <div className="datagrid-item"><div className="datagrid-title">Role</div><div className="datagrid-content" style={{ textTransform: 'capitalize' }}>{(m.role || '').replace(/_/g, ' ')}</div></div>
                    <div className="datagrid-item"><div className="datagrid-title">Phone</div><div className="datagrid-content">{m.phone || '—'}</div></div>
                    <div className="datagrid-item"><div className="datagrid-title">Last Login</div><div className="datagrid-content">{m.last_login ? formatDate(m.last_login.split('T')[0], { month: 'short', day: 'numeric' }) : 'Never'}</div></div>
                  </div>
                  {isAdmin && m.id !== user.id && (
                    <div className="mt-3 pt-3 border-top">
                      <button className={`btn btn-sm ${m.is_active ? 'btn-outline-danger' : 'btn-outline-success'}`} onClick={() => toggleActive(m)}>
                        {m.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Team Member" wide>
        <div className="row g-3">
          {[{ label: 'Name', key: 'name' }, { label: 'Email', key: 'email', type: 'email' }, { label: 'Title', key: 'title' },
            { label: 'Phone', key: 'phone' }, { label: 'Password', key: 'password', type: 'password' }].map(f => (
            <div key={f.key} className="col-md-6"><label className="form-label">{f.label}</label>
              <input type={f.type || 'text'} className="form-control" value={formData[f.key] || ''} onChange={e => setFormData({ ...formData, [f.key]: e.target.value })} /></div>))}
          <div className="col-md-6"><label className="form-label">Role</label>
            <select className="form-select" value={formData.role || ''} onChange={e => setFormData({ ...formData, role: e.target.value })}>
              <option value="">Select...</option>
              {['admin', 'dos', 'sales_manager', 'sales_coordinator', 'front_desk'].map(r => <option key={r} value={r}>{r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
            </select></div>
        </div>
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button className="btn" onClick={() => setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={handleCreate}>{saving ? 'Creating...' : 'Add Member'}</button>
        </div>
      </Modal>
    </>
  )
}
